import { useCallback, useMemo } from 'react';
import Input from 'app/components/Input/Input';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { ICourseNodeDAO } from 'types/domain/course';
import CourseNodeStatusToggle from 'app/features/Course/CourseNode/CourseNodeStatusToggle';
import { useCopyLink } from 'core/hooks/useCopyLink';
import { H5 } from 'app/components/Typography/Typography';
import CourseNodeShare from '../../Course/CourseNode/Share/CourseNodeShare';
import { useIsSuperAdmin } from 'core/hooks/useIsAdmin';
import Toggle from 'app/components/Toggle/Toggle';
import { ModuleStatus, ModuleType } from 'types/domain/module';
import { useSave } from 'core/hooks/useSave';
import { EntityTypes } from 'constants/entities';
import { useAppSelector } from 'core/store/hooks';
import { selectModuleDAO } from 'core/selectors/product/module';
import ThemeSelect from 'app/features/Theme/ThemeSelect';

interface ModuleShareProps {
  courseNode: ICourseNodeDAO;
}

const COMPONENT_ID_LEARNERS = 'module-share-learners';
export const COMPONENT_ID_ADD_LEARNER = 'module-share-add-learner';

const ModuleShare: React.FC<ModuleShareProps> = ({ courseNode }) => {
  const { copyLinkToClipboard } = useCopyLink();
  const domain = /:\/\/([^/]+)/.exec(window.location.href)?.[1] || 'devco';
  const isSuperAdmin = useIsSuperAdmin();
  const saveModule = useSave(EntityTypes.MODULE);
  const saveCourseNode = useSave(EntityTypes.COURSE_NODE);
  const module = useAppSelector(state => selectModuleDAO(state, courseNode?.moduleId));
  const shareUrl = useMemo(() => {
    if (courseNode) return `https://${domain}/en/${courseNode?.prettyToken ?? courseNode?.token}`;
    return null;
  }, [courseNode, domain]);

  const onCopyUrl = useCallback(() => {
    if (shareUrl) copyLinkToClipboard(shareUrl);
  }, [shareUrl, copyLinkToClipboard]);

  return (
    <>
      <div className="flex items-center mb-10">
        <Input readOnly autoResize label="Copy this link to share" variant="form" value={shareUrl} />
        <ClipboardDocumentIcon
          onClick={onCopyUrl}
          className="w-6 h-6 ml-2 mr-2 hover:text-gray-500 hover:border hover:border-white rounded cursor-pointer"
        />
      </div>
      <H5 className="my-5">Access status</H5>
      <CourseNodeStatusToggle courseNode={courseNode} className="my-5" />
      <H5 className="my-5">Theme</H5>
      <ThemeSelect courseNodeId={courseNode?.id} className="" />

      {module?.type === ModuleType.ASSESSMENT_RANDOM && (
        <div className="flex flex-col gap-2 my-5">
          <H5 className="my-5">Question limit</H5>
          <Input
            type="number"
            variant="form"
            value={courseNode.limit != null ? String(courseNode.limit) : ''}
            onChangeText={v => saveCourseNode({ id: courseNode.id, limit: v === '' ? null : Number(v) })}
          />
        </div>
      )}
      {[ModuleType.ASSESSMENT_RANDOM, ModuleType.ASSESSMENT].includes(module?.type) && (
        <div className="flex flex-col gap-2 my-5">
          <H5 className="my-5">Pass rate</H5>
          <Input
            type="number"
            variant="form"
            value={courseNode.value != null ? String(courseNode.value) : ''}
            onChangeText={v => saveCourseNode({ id: courseNode.id, value: v === '' ? null : Number(v) })}
          />
        </div>
      )}

      <CourseNodeShare
        className="mt-10 flex-col sm:flex-row w-full gap-10"
        courseNodeId={courseNode?.id}
        courseId={courseNode?.courseId}
        componentId={COMPONENT_ID_LEARNERS}
        typeaheadComponentId={COMPONENT_ID_ADD_LEARNER}
      />

      {isSuperAdmin && (
        <div className="flex gap-5">
          <H5>Make template</H5>
          <Toggle
            checked={module?.status === ModuleStatus.TEMPLATE}
            onChange={checked =>
              saveModule({ id: courseNode.moduleId, status: checked ? ModuleStatus.TEMPLATE : ModuleStatus.ACTIVE })
            }
          />
        </div>
      )}
    </>
  );
};

export default ModuleShare;
